import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { colors, typography } from '../../theme/designSystem';
import SavingOverlay from '../SavingOverlay';
import { patientsService, QuickRegisterResult } from '../../services/patientsService';
import { formatCpf, isValidCpf, cpfDigits, isValidEmail, isFutureBirthDate, todayIso } from '../../utils/documents';
import { useAuth } from '../../contexts/AuthContext';
import PhoneWithCountryInput from '../PhoneWithCountryInput';
import { EMPTY_PHONE, PhoneValue, phoneNational, toE164Digits, whatsAppLink } from '../../utils/phone';

// Cadastro rápido de paciente (RN-112c) — componente reutilizável: qualquer página
// pode abrir o modal (lista de pacientes, agenda etc.). O paciente nasce no Bubble
// via backend (RN-302); onSuccess recebe o id local criado. Ao salvar, o cadastro
// completo abre em NOVA ABA já pré-preenchido (RN-112d) para continuar o preenchimento.

const NAME_MAX = 100;
const DOCUMENT_MAX = 14;
const EMAIL_MAX = 100;
const NOTES_MAX = 500;

interface QuickPatientRegisterModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (result: QuickRegisterResult) => void;
}

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: '1rem',
    backgroundColor: 'white',
    '& fieldset': { borderColor: '#ced4da', legend: { maxWidth: '100%' } },
    '&:hover fieldset': { borderColor: '#ced4da' },
    '&.Mui-focused fieldset': {
      borderColor: '#03B4C6',
      boxShadow: '0 0 0 3px rgba(3, 180, 198, 0.1)',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.95rem',
    color: '#6c757d',
    backgroundColor: 'white',
    paddingLeft: '4px',
    paddingRight: '4px',
    '&.Mui-focused': { color: '#03B4C6' },
  },
};

const emptyForm = {
  patientName: '',
  birthDate: '',
  document: '',
  email: '',
  responsibleName: '',
  contactsAreResponsible: true,
  sendRegistrationEmail: true,
  notes: '',
};

const QuickPatientRegisterModal: React.FC<QuickPatientRegisterModalProps> = ({ open, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [form, setForm] = useState(emptyForm);
  const [phone, setPhone] = useState<PhoneValue>(EMPTY_PHONE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Link criado (RN-112f): exibido para cópia/WhatsApp — a secretaria pode enviar na hora
  const [createdLink, setCreatedLink] = useState<string | null>(null);
  // Dígitos E.164 (DDI + número) para o wa.me
  const [createdPhone, setCreatedPhone] = useState<string | null>(null);
  const [createdEmailSent, setCreatedEmailSent] = useState(false);
  const [copied, setCopied] = useState(false);

  const hasContactEmail = form.email.trim().length > 0;
  // CPF: formato validado na digitação; duplicidade é ALERTA (não bloqueia) — RN-112g
  const [documentWarning, setDocumentWarning] = useState('');
  const [documentInvalid, setDocumentInvalid] = useState(false);

  const handleDocumentBlur = async () => {
    const value = form.document.trim();
    setDocumentWarning('');
    setDocumentInvalid(false);
    if (!value) return;
    if (!isValidCpf(value)) {
      setDocumentInvalid(true);
      return;
    }
    try {
      const check = await patientsService.checkDocument(cpfDigits(value));
      if (check.exists) {
        setDocumentWarning(`Atenção: já existe paciente com este CPF${check.existingPatientName ? ` (${check.existingPatientName})` : ''}. O cadastro pode continuar.`);
      }
    } catch {
      // checagem é auxiliar — falha não impede o cadastro
    }
  };

  const setField = (field: keyof typeof emptyForm, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleClose = () => {
    if (saving) return;
    setForm(emptyForm);
    setPhone(EMPTY_PHONE);
    setError('');
    setCreatedLink(null);
    setCreatedPhone(null);
    setCreatedEmailSent(false);
    setCopied(false);
    onClose();
  };

  const handleSave = async () => {
    if (saving) return;
    if (!form.patientName.trim() || !form.birthDate || !form.email.trim() || !phoneNational(phone)) {
      setError('Nome completo, data de nascimento, WhatsApp e email são obrigatórios.');
      return;
    }
    if (isFutureBirthDate(form.birthDate)) {
      setError('A data de nascimento não pode ser posterior à data de hoje.');
      return;
    }
    if (form.contactsAreResponsible && !form.responsibleName.trim()) {
      setError('Informe o nome do responsável (os contatos são do responsável).');
      return;
    }
    if (form.document.trim() && !isValidCpf(form.document)) {
      setError('CPF inválido. Confira os números informados.');
      return;
    }
    if (!isValidEmail(form.email)) {
      setError('Email inválido. Confira o endereço informado.');
      return;
    }

    setSaving(true);
    try {
      const result = await patientsService.quickRegister({
        patientName: form.patientName.trim(),
        birthDate: form.birthDate || null,
        document: form.document.trim() || null,
        phone: phoneNational(phone),
        phoneDdi: phone.ddi,
        email: form.email.trim() || null,
        responsibleName: form.responsibleName.trim() || null,
        contactsAreResponsible: form.contactsAreResponsible,
        notes: form.notes.trim() || null,
        sendRegistrationEmail: form.sendRegistrationEmail && hasContactEmail,
        clinicAlias: user?.alias || null,
      });
      onSuccess(result);
      if (result.intakeLink) {
        // Mantém o modal aberto exibindo o link para cópia/WhatsApp
        setCreatedPhone(toE164Digits(phone.ddi, phone.ddd, phone.number));
        setCreatedEmailSent(result.emailSent);
        setCreatedLink(result.intakeLink);
        setForm(emptyForm);
        setPhone(EMPTY_PHONE);
      } else {
        setForm(emptyForm);
        setPhone(EMPTY_PHONE);
        onClose();
        window.open(`${window.location.origin}${window.location.pathname}?page=patient-register&id=${result.id}`, '_blank');
      }
    } catch (err: any) {
      setError(err?.response?.data?.description || 'Erro ao cadastrar o paciente. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
          maxWidth: '560px',
          position: 'relative',
          overflow: 'hidden',
        },
      }}
    >
      <SavingOverlay open={saving} message="Cadastrando paciente..." />
      <DialogTitle
        sx={{
          backgroundColor: colors.primary,
          color: colors.white,
          padding: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h6"
          component="h3"
          sx={{ fontSize: '1.4rem', fontWeight: typography.fontWeight.semibold, margin: 0 }}
        >
          Cadastro rápido de paciente
        </Typography>
        <IconButton
          onClick={handleClose}
          disabled={saving}
          sx={{
            color: colors.white,
            padding: '0.25rem',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: '1.5rem !important', paddingTop: '2rem !important' }}>
        {createdLink ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body1" sx={{ color: '#495057' }}>
            Paciente cadastrado!{' '}
            {createdEmailSent
              ? 'O email com o link de preenchimento foi enviado.'
              : 'O email não foi enviado neste ambiente.'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#6c757d' }}>
            Link de preenchimento (válido por 72 horas, uso único) — copie ou envie por WhatsApp:
          </Typography>
          <TextField
            value={createdLink}
            size="small"
            fullWidth
            InputProps={{ readOnly: true }}
            onFocus={(e) => e.target.select()}
            sx={fieldSx}
          />
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={() => {
                navigator.clipboard.writeText(createdLink).then(() => setCopied(true));
              }}
              sx={{
                backgroundColor: '#03B4C6', color: 'white', textTransform: 'none',
                '&:hover': { backgroundColor: '#029AAB' },
              }}
            >
              {copied ? 'Copiado!' : 'Copiar link'}
            </Button>
            {createdPhone && (
              <Button
                variant="contained"
                onClick={() => {
                  const link = whatsAppLink(createdPhone, `Olá! Para completar o cadastro, acesse: ${createdLink}`);
                  if (link) window.open(link, '_blank');
                }}
                sx={{
                  backgroundColor: '#25D366', color: 'white', textTransform: 'none',
                  '&:hover': { backgroundColor: '#1da851' },
                }}
              >
                Enviar por WhatsApp
              </Button>
            )}
          </Box>
        </Box>
        ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Nome completo"
            value={form.patientName}
            onChange={(e) => setField('patientName', e.target.value)}
            size="small"
            fullWidth
            required
            inputProps={{ maxLength: NAME_MAX }}
            InputLabelProps={{ shrink: true }}
            sx={fieldSx}
          />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Data de nascimento"
              type="date"
              required
              value={form.birthDate}
              onChange={(e) => setField('birthDate', e.target.value)}
              size="small"
              inputProps={{ max: todayIso() }}
              error={isFutureBirthDate(form.birthDate)}
              helperText={isFutureBirthDate(form.birthDate) ? 'A data não pode ser futura' : ''}
              InputLabelProps={{ shrink: true }}
              sx={{ ...fieldSx, flex: 1, minWidth: '160px' }}
            />
            <TextField
              label="CPF"
              value={form.document}
              onChange={(e) => setField('document', formatCpf(e.target.value))}
              onBlur={handleDocumentBlur}
              error={documentInvalid}
              helperText={documentInvalid ? 'CPF inválido' : ' '}
              size="small"
              inputProps={{ maxLength: DOCUMENT_MAX }}
              InputLabelProps={{ shrink: true }}
              sx={{ ...fieldSx, flex: 1, minWidth: '160px' }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <PhoneWithCountryInput
              label="WhatsApp"
              required
              value={phone}
              onChange={(value) => { setPhone(value); setError(''); }}
              placeholder="11999999999"
              fieldSx={fieldSx}
              labelProps={{ shrink: true }}
              sx={{ flex: 1, minWidth: '230px' }}
            />
            <TextField
              label="Email"
              type="email"
              required
              value={form.email}
              error={!!form.email.trim() && !isValidEmail(form.email)}
              helperText={form.email.trim() && !isValidEmail(form.email) ? 'Email inválido' : ' '}
              onChange={(e) => setField('email', e.target.value)}
              size="small"
              inputProps={{ maxLength: EMAIL_MAX }}
              InputLabelProps={{ shrink: true }}
              sx={{ ...fieldSx, flex: 1, minWidth: '160px' }}
            />
          </Box>
          <TextField
            label="Nome do responsável"
            value={form.responsibleName}
            onChange={(e) => setField('responsibleName', e.target.value)}
            size="small"
            fullWidth
            required={form.contactsAreResponsible}
            inputProps={{ maxLength: NAME_MAX }}
            InputLabelProps={{ shrink: true }}
            sx={fieldSx}
          />
          <FormControlLabel
            sx={{ alignItems: 'flex-start', ml: 0, mr: 0 }}
            control={
              <Checkbox
                checked={form.contactsAreResponsible}
                onChange={(e) => setField('contactsAreResponsible', e.target.checked)}
                sx={{ pt: 0, pl: 0, pr: 1, '&.Mui-checked': { color: '#03B4C6' } }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: '#495057' }}>
                O WhatsApp e o email informados são do responsável (desmarque se forem do próprio paciente)
              </Typography>
            }
          />
          <FormControlLabel
            sx={{ alignItems: 'flex-start', ml: 0, mr: 0 }}
            control={
              <Checkbox
                checked={form.sendRegistrationEmail && hasContactEmail}
                disabled={!hasContactEmail}
                onChange={(e) => setField('sendRegistrationEmail', e.target.checked)}
                sx={{ pt: 0, pl: 0, pr: 1, '&.Mui-checked': { color: '#03B4C6' } }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: hasContactEmail ? '#495057' : '#adb5bd' }}>
                Enviar e-mail para preenchimento do cadastro (link válido por 72 horas)
                {!hasContactEmail && ' — informe um email para habilitar'}
              </Typography>
            }
          />
          <TextField
            label="Observação"
            value={form.notes}
            onChange={(e) => setField('notes', e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={3}
            inputProps={{ maxLength: NOTES_MAX }}
            InputLabelProps={{ shrink: true }}
            helperText={`${form.notes.length}/${NOTES_MAX}`}
            sx={fieldSx}
          />
          {documentWarning && (
            <Typography
              variant="body2"
              sx={{
                padding: '0.75rem', backgroundColor: '#fff3cd', color: '#856404',
                borderRadius: '4px', border: '1px solid #ffeaa7',
              }}
            >
              {documentWarning}
            </Typography>
          )}
          {error && (
            <Typography variant="body2" sx={{ color: '#dc3545' }}>
              {error}
            </Typography>
          )}
        </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ padding: '0.5rem 1.5rem 1rem', gap: '0.5rem' }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={saving}
          sx={{
            color: '#6c757d',
            borderColor: '#6c757d',
            textTransform: 'none',
            padding: '0.5rem 1.5rem',
            '&:hover': { borderColor: '#5a6268', backgroundColor: '#f8f9fa' },
          }}
        >
          {createdLink ? 'Fechar' : 'Cancelar'}
        </Button>
        {!createdLink && (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            sx={{
              backgroundColor: '#03B4C6',
              color: 'white',
              textTransform: 'none',
              padding: '0.5rem 1.5rem',
              '&:hover': { backgroundColor: '#029AAB' },
            }}
          >
            Cadastrar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default QuickPatientRegisterModal;
